import * as React from 'react';

import { CardStatusTag } from '@coursera/cds-core';
import { TranslationIcon } from '@coursera/cds-icons';

import _t from 'i18n!nls/product-card';

export const BADGE_NAMES = {
  FREE: 'Free',
  FREE_TRIAL: 'Free Trial',
  PREVIEW: 'Preview',
  FULLY_TRANSLATED: 'Fully Translated',
  SUBTITLES: 'Subtitles',
  TOP_INSTRUCTOR: 'Top Instructor',
  SPONSORED: 'Sponsored',
};

const isLanguageModeTag = (tagName: string): boolean => {
  return tagName === BADGE_NAMES.FULLY_TRANSLATED || tagName === BADGE_NAMES.SUBTITLES;
};

/**
 * Reorders tags to prioritize language mode labels (Fully Translated, Subtitles) first
 * @param tags - Array of React elements representing tags
 * @param tagNames - Array of tag name strings corresponding to tags
 * @returns Object with prioritized tags and tagNames arrays
 */
export const prioritizeLanguageModeTags = (
  tags: React.ReactElement[],
  tagNames: string[]
): { prioritizedTags: React.ReactElement[]; prioritizedTagNames: string[] } => {
  const languageModeTags: React.ReactElement[] = [];
  const languageModeTagNames: string[] = [];
  const otherTags: React.ReactElement[] = [];
  const otherTagNames: string[] = [];

  tags.forEach((tag, index) => {
    const tagName = tagNames[index] || '';
    if (isLanguageModeTag(tagName)) {
      languageModeTags.push(tag);
      languageModeTagNames.push(tagName);
    } else {
      otherTags.push(tag);
      otherTagNames.push(tagName);
    }
  });

  // Rebuild arrays with proper prioritization: Language Mode Labels first, then everything else
  const prioritizedTags = [...languageModeTags, ...otherTags];
  const prioritizedTagNames = [...languageModeTagNames, ...otherTagNames];

  return { prioritizedTags, prioritizedTagNames };
};

export const getProductCardStatusTags = ({
  isNew,
  statusTags,
  slug,
  hasTopInstructor,
  sponsored,
}: {
  isNew?: boolean;
  statusTags?: string[] | null;
  slug?: string;
  hasTopInstructor?: boolean;
  sponsored?: boolean;
}) => {
  let tags = [];
  let tagNames = [];

  if (isNew) {
    tags.push(<CardStatusTag key="new">{_t('New')}</CardStatusTag>);
    tagNames.push('New');
  }

  if (hasTopInstructor) {
    tags.push(<CardStatusTag key="top-instructor">{_t('Top instructor')}</CardStatusTag>);
    tagNames.push('Top Instructor');
  }

  if (statusTags && statusTags.length > 0) {
    statusTags.forEach((statusTag) => {
      switch (statusTag) {
        case BADGE_NAMES.FREE:
          tags.push(<CardStatusTag key={statusTag}>{_t('Free')}</CardStatusTag>);
          tagNames.push('Free');
          break;
        case BADGE_NAMES.FREE_TRIAL:
          // Request from Google to temporarily hide this badge for google-ai prof cert
          // More context: https://coursera.atlassian.net/browse/MERCH-2631
          if (slug !== 'google-ai') {
            tags.push(<CardStatusTag key={statusTag}>{_t('Free trial')}</CardStatusTag>);
            tagNames.push('Free Trial');
          }
          break;
        case BADGE_NAMES.PREVIEW:
          tags.push(<CardStatusTag key={statusTag}>{_t('Preview')}</CardStatusTag>);
          tagNames.push('Preview');
          break;
        case BADGE_NAMES.FULLY_TRANSLATED:
          tags.push(
            <CardStatusTag key={statusTag} icon={<TranslationIcon />}>
              {_t('Fully translated')}
            </CardStatusTag>
          );
          tagNames.push('Fully Translated');
          break;
        case BADGE_NAMES.SUBTITLES:
          tags.push(
            <CardStatusTag key={statusTag} icon={<TranslationIcon />}>
              {_t('Subtitles')}
            </CardStatusTag>
          );
          tagNames.push('Subtitles');
          break;
        default:
        // no ops
      }
    });

    // Prioritize language mode labels if they exist
    const hasLanguageModeLabels = statusTags.some(isLanguageModeTag);

    if (hasLanguageModeLabels) {
      const { prioritizedTags, prioritizedTagNames } = prioritizeLanguageModeTags(tags, tagNames);
      tags = prioritizedTags;
      tagNames = prioritizedTagNames;
    }
  }

  // A sponsored (partner-funded) placement is labeled first on the thumbnail overlay so the
  // "Sponsored" disclosure leads the status tags regardless of any other badges on the card.
  if (sponsored) {
    tags = [<CardStatusTag key="sponsored">{_t('Sponsored')}</CardStatusTag>, ...tags];
    tagNames = [BADGE_NAMES.SPONSORED, ...tagNames];
  }

  return { tags, tagNames };
};
